import React from 'react';
import { IoCopyOutline } from 'react-icons/io5';

import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { shortenString } from 'src/components/Table/helpers';

import { CellCopyButton, CellWrap } from './styled';

type CopyableCellProps = {
  value?: string | null;
  /** Names the field in the confirmation toast, e.g. "Address copied". */
  label?: string;
  /** Truncate the middle of long values (default). */
  shorten?: boolean;
  placeholder?: string;
};

/**
 * A table cell value with an inline copy control. Row clicks usually open a
 * details modal, so the button stops propagation.
 */
export const CopyableCell: React.FC<CopyableCellProps> = ({
  value,
  label,
  shorten = true,
  placeholder = '—',
}) => {
  const dispatch = useDispatch();
  const text = value ? String(value) : '';

  if (!text || text === placeholder) return <span>{placeholder}</span>;

  const copy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      dispatch(
        setAppAlert({ message: label ? `${label} copied` : 'Copied', isSuccess: true }),
      );
    } catch {
      dispatch(setAppAlert({ message: 'Failed to copy', isSuccess: false }));
    }
  };

  return (
    <CellWrap>
      <span title={text}>{shorten ? shortenString(text) : text}</span>
      <CellCopyButton
        type="button"
        aria-label={label ? `Copy ${label.toLowerCase()}` : 'Copy'}
        title={label ? `Copy ${label.toLowerCase()}` : 'Copy'}
        onClick={copy}
      >
        <IoCopyOutline size={14} />
      </CellCopyButton>
    </CellWrap>
  );
};
